import { useEffect, useState } from 'react';
import { AutoAttendance } from '../attendance/AutoAttendance';
import { UrgentAlert } from '../notifications/UrgentAlert';
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useBadges } from '../../shared/badges';
import { onLive, startLive } from '../../shared/live';
import { syncPush } from '../../shared/push/push';
import { Logo } from '../../shared/ui/components';
import { useMe, useSession } from '../session/SessionProvider';
import { NotificationBell } from '../notifications/NotificationBell';
import { SearchButton } from '../search/SearchPage';
import { ContextSwitcher, SupportBanner } from './ContextSwitcher';
import { inDesktopApp } from '../presentation/screens';
import { DeviceLayout } from './DeviceLayout';
import { HelpButton } from './HelpButton';
import { LiveNowBanner } from '../live/LivePage';
import { badgeFor, Icon, isActive, useBottomMenu, useMenuParts, type NavItem } from './nav';

/**
 * Shell de la aplicación, mobile-first:
 * - celular: barra superior + navegación inferior;
 * - tablet/escritorio: navegación lateral.
 * Las opciones visibles dependen de los permisos efectivos (solo UX; el backend decide).
 */
const NAV_PINNED_KEY = 'keora.menu.abierto';

export function AppLayout() {
  // El equipo de multimedia (la computadora de proyección) tiene su propio menú, sin nada personal.
  const me = useMe();
  if (me.isDevice) return <DeviceLayout />;
  // En la app KEORA Multimedia nunca se abre el panel de una persona: primero se vincula.
  if (inDesktopApp()) return <Navigate to="/equipo" replace />;
  return <PersonLayout />;
}

function PersonLayout() {
  const { logout, refresh } = useSession();
  const navigate = useNavigate();
  const me = useMe();
  const badges = useBadges(me);

  // En vivo: avisos y globitos sin recargar; si aprueban mi solicitud, la app se carga con mi iglesia.
  useEffect(() => {
    const stop = startLive();
    const off = onLive((e) => {
      if (e.kind === 'membership.approved') void refresh();
    });
    return () => {
      off();
      stop();
    };
  }, [refresh]);

  // Avisos al celular: si este dispositivo ya dio permiso, queda suscrito a nombre de quien entró.
  useEffect(() => {
    void syncPush(me.personId);
  }, [me.personId]);

  // La primera vez el menú lateral sale abierto (con nombres); si la persona lo encoge, se recuerda.
  const [pinned, setPinned] = useState(() => {
    try {
      return localStorage.getItem(NAV_PINNED_KEY) !== '0';
    } catch {
      return true;
    }
  });
  function togglePinned() {
    setPinned((v) => {
      try {
        localStorage.setItem(NAV_PINNED_KEY, v ? '0' : '1');
      } catch {
        // Sin almacenamiento: solo dura esta visita.
      }
      return !v;
    });
  }

  const bottom = useBottomMenu();
  const parts = useMenuParts();
  const { pathname } = useLocation();

  function sideLink(item: NavItem) {
    const active = isActive(item, pathname);
    return (
      <Link key={item.to} to={item.to} title={item.label} className={`navlink${active ? ' active' : ''}`} aria-current={active ? 'page' : undefined}>
        <Icon name={item.icon} />
        <span>{item.label}</span>
        {badgeFor(item, badges) > 0 ? <span className="nav-badge" aria-label={`${badgeFor(item, badges)} pendientes`}>{badgeFor(item, badges)}</span> : null}
      </Link>
    );
  }

  async function onLogout() {
    await logout();
    navigate('/', { replace: true });
  }

  return (
    <div className={`shell${pinned ? '' : ' shell--compact'}`}>
      <a href="#contenido" className="skip-link">
        Saltar al contenido
      </a>
      <header className="topbar">
        <NavLink to="/app" className="topbar__brand" aria-label="Inicio">
          <Logo />
        </NavLink>
        <ContextSwitcher />
        <SearchButton />
        <NotificationBell />
      </header>

      <nav className="sidenav" aria-label="Principal">
        <button type="button" className="navlink navlink--button sidenav__toggle" onClick={togglePinned} aria-pressed={pinned} title={pinned ? 'Encoger menú' : 'Dejar menú abierto'}>
          <Icon name={pinned ? 'collapse' : 'expand'} />
          <span>{pinned ? 'Encoger menú' : 'Dejar abierto'}</span>
        </button>
        {parts.daily.map((item) => sideLink(item))}
        {parts.sections.length ? (
          <>
            <p className="sidenav__label" aria-hidden="true">
              Secciones
            </p>
            {parts.sections.map((item) => sideLink(item))}
          </>
        ) : null}
        <div className="sidenav__foot">
          {parts.profile ? sideLink(parts.profile) : null}
          <button type="button" className="navlink navlink--button" onClick={onLogout} title="Salir">
            <Icon name="logout" />
            <span>Salir</span>
          </button>
        </div>
      </nav>

      <main id="contenido" className="content">
        <SupportBanner />
        <LiveNowBanner />
        <AutoAttendance />
        <Outlet />
        <UrgentAlert />
      </main>

      <HelpButton />

      <nav className={`bottomnav${bottom.bar.length > 5 ? ' bottomnav--six' : ''}`} aria-label="Principal (móvil)">
        {bottom.bar.map((item) => {
          const active = item.to === '/app/mas' ? pathname === '/app/mas' : isActive(item, pathname);
          // «Más» suma lo pendiente de todo lo que guarda.
          const badge = item.to === '/app/mas' ? badgeFor(bottom.rest, badges) : badgeFor(item, badges);
          return (
            <Link key={item.to} to={item.to} className={`bottomnav__item${active ? ' active' : ''}`} aria-current={active ? 'page' : undefined}>
              <span className="bottomnav__icon">
                <Icon name={item.icon} />
                {badge > 0 ? <span className="nav-badge nav-badge--dot" aria-label={`${badge} pendientes`}>{badge}</span> : null}
              </span>
              <span>{item.short}</span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
